import { Clock, Loader2, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { FlowScheduler } from "@/components/flows/FlowScheduler";
import type { SyncSchedule } from "@/types";
import { formatSyncCadence } from "./utils";

type SchedulerValue = Parameters<React.ComponentProps<typeof FlowScheduler>["onSave"]>[0];

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function toSyncSchedule(value: SchedulerValue): SyncSchedule {
  const schedule: SyncSchedule = {
    type: value.schedule_type as SyncSchedule["type"],
    run_at_time: value.run_at_time,
    timezone: value.timezone,
  };
  if (value.days_of_week) schedule.days_of_week = value.days_of_week;
  if (value.interval_days) schedule.interval_days = value.interval_days;
  return schedule;
}

function toSchedulerValue(schedule: SyncSchedule | null | undefined): SchedulerValue {
  return {
    schedule_type: schedule?.type ?? "daily",
    run_at_time: schedule?.run_at_time ?? "09:00",
    timezone: schedule?.timezone ?? browserTimezone(),
    days_of_week: schedule?.days_of_week,
    interval_days: schedule?.interval_days,
  };
}

export interface SyncScheduleSectionProps {
  schedule: SyncSchedule | null | undefined;
  onSave: (schedule: SyncSchedule) => Promise<void> | void;
  onRemove: () => Promise<void> | void;
  disabled?: boolean;
}

export function SyncScheduleSection({ schedule, onSave, onRemove, disabled }: Readonly<SyncScheduleSectionProps>) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<void> | void) => {
    setBusy(true);
    const succeeded = await Promise.resolve()
      .then(action)
      .then(
        () => true,
        () => false,
      );
    setBusy(false);
    if (succeeded) setEditing(false);
  };

  if (editing) {
    return (
      <FlowScheduler
        title="Schedule Sync"
        subtitle="Keep this source up to date automatically"
        showStartDate={false}
        showInputPrompt={false}
        initialSchedule={toSchedulerValue(schedule)}
        onCancel={() => setEditing(false)}
        onSave={(value) => void run(() => onSave(toSyncSchedule(value)))}
      />
    );
  }

  const locked = disabled || busy;

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-content-secondary">Schedule</span>
        {!schedule && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            disabled={locked}
            className="text-sm text-brand-primary hover:underline disabled:opacity-50"
          >
            + Add Schedule
          </button>
        )}
      </div>

      {schedule ? (
        <div className="mt-2 flex items-center justify-between gap-3 px-3 py-2 bg-surface-secondary rounded-lg">
          <p className="flex items-center gap-1.5 text-sm text-content-primary min-w-0">
            <Clock className="h-4 w-4 text-brand-primary shrink-0" />
            <span className="truncate">{formatSyncCadence(schedule)}</span>
          </p>
          <div className="flex items-center gap-1 shrink-0">
            {busy && <Loader2 className="h-4 w-4 animate-spin text-content-tertiary" />}
            <button
              type="button"
              onClick={() => setEditing(true)}
              disabled={locked}
              aria-label="Edit schedule"
              className="p-1.5 text-content-tertiary hover:text-content-primary rounded-sm disabled:opacity-50"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => void run(onRemove)}
              disabled={locked}
              aria-label="Remove schedule"
              className="p-1.5 text-content-tertiary hover:text-red-400 rounded-sm disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-1 text-xs text-content-tertiary">
          No recurring schedule — syncs only when triggered manually.
        </p>
      )}
    </div>
  );
}
